import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  User, Briefcase, Users, Globe, Star, Building2, Share2,
  Plus, Trash2, Save, CheckCircle2, AlertCircle, Loader2,
  Wrench, Zap, Truck, Paintbrush, Cable, ArrowUpDown, PenLine, GalleryHorizontal,
  Eye, Wand2, ExternalLink, Bell, MessageSquare, BriefcaseIcon, ThumbsUp, ThumbsDown,
  Trophy, Diamond, PartyPopper, Coins, Sparkles, Clock, CreditCard,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProfileImageUpload, resolveAssetUrl } from '../../components/contractor/ProfileImageUpload';
import PortfolioGallery, { resolveGalleryUrl as resolveGalleryUrlHelper } from '../../components/contractor/PortfolioGallery';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Smart Fair Picker ─────────────────────────────────────────────────────────
function FairPicker({ selected, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/public/fairs?search=${encodeURIComponent(query)}&limit=8`);
        const d = await r.json();
        setResults(d.items || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const add = (fair) => {
    if (!selected.includes(fair.name)) onChange([...selected, fair.name]);
    setQuery(''); setResults([]); setOpen(false);
  };
  const remove = (name) => onChange(selected.filter(s => s !== name));

  return (
    <div ref={ref} className="space-y-2">
      <p className="text-xs text-gray-400">Add the fairs you attend — they appear as badges on your public profile</p>
      {/* Selected tags */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map(name => (
            <span key={name} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1 rounded-full border border-blue-200">
              {name}
              <button onClick={() => remove(name)} className="hover:text-red-500 ml-0.5">×</button>
            </span>
          ))}
        </div>
      )}
      {/* Single search + manual add input */}
      <div className="relative">
        <input
          data-testid="fair-picker-search"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          placeholder="Search fairs… (e.g. Hannover Messe, GITEX) or type and press Enter"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' && query.trim() && !open) {
              if (!selected.includes(query.trim())) onChange([...selected, query.trim()]);
              setQuery(''); setResults([]); setOpen(false);
            }
          }}
        />
        {loading && <div className="absolute right-3 top-2.5 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
        {open && results.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
            {results.map(f => (
              <button key={f.fair_id} onClick={() => add(f)}
                className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0">
                <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                <p className="text-xs text-gray-400">{f.city}{f.country ? `, ${f.country}` : ''} {f.start_date ? `· ${f.start_date.slice(0,7)}` : ''}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

const SERVICE_OPTIONS = ['Design', 'Build', 'Rental', 'Logistics', 'AV', 'Electrical', 'Rigging', 'Graphics', 'Furniture'];
const ICON_OPTIONS = ['Wrench', 'Zap', 'Truck', 'Paintbrush', 'Cable', 'ArrowUpDown', 'Star', 'Building2'];
const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'AED', 'TRY'];

const TABS = [
  { id: 'overview',       label: 'Overview',       icon: PenLine },
  { id: 'portfolio',      label: 'Portfolio',      icon: GalleryHorizontal },
  { id: 'services',       label: 'Services',       icon: Wrench },
  { id: 'team',           label: 'Team',           icon: Users },
  { id: 'coverage',       label: 'Coverage',       icon: Globe },
  { id: 'company',        label: 'Company',        icon: Building2 },
  { id: 'social',         label: 'Social',         icon: Share2 },
  { id: 'notifications',  label: 'Bildirimler',    icon: Bell },
  { id: 'payment',        label: 'Ödeme Bilgileri', icon: CreditCard },
];

const emptyPortfolioItem = () => ({ id: `pf_${Date.now()}`, title: '', client: '', image_url: '', images: [], sqm: '', year: new Date().getFullYear(), tags: [] });
const emptyTeamMember   = () => ({ id: `tm_${Date.now()}`, name: '', role: '', bio: '', avatar_url: '', linkedin: '', instagram: '', facebook: '' });
const emptyServiceDetail= () => ({ id: `sv_${Date.now()}`, title: '', desc: '', price: '', icon_name: 'Wrench' });
const emptyCoverageCountry = () => ({ name: '', flag: '', cities: [], primary: false });

// ── Confetti Animation ────────────────────────────────────────────────────────
const CONFETTI_COLORS = ['#16a34a', '#f97316', '#3b82f6', '#a855f7', '#ec4899', '#eab308', '#06b6d4'];
const ConfettiBlast = ({ onDone }) => {
  const particles = useRef(
    Array.from({ length: 50 }, (_, i) => ({
      id: i,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      left: `${(i / 50) * 100 + (Math.random() - 0.5) * 8}%`,
      delay: `${(i / 50) * 0.6}s`,
      duration: `${1.0 + (i % 5) * 0.15}s`,
      size: `${6 + (i % 4) * 2}px`,
      shape: i % 3 === 0 ? '50%' : i % 3 === 1 ? '0' : '2px',
    }))
  ).current;
  useEffect(() => { const t = setTimeout(onDone, 2500); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute', top: '-20px', left: p.left,
          width: p.size, height: p.size, backgroundColor: p.color,
          borderRadius: p.shape, animationDelay: p.delay, animationDuration: p.duration,
          animation: `confettiFall ${p.duration} ${p.delay} ease-in forwards`,
        }} />
      ))}
      <style>{`@keyframes confettiFall { 0% { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; } 100% { transform: translateY(105vh) rotate(720deg) scale(0.8); opacity: 0; } }`}</style>
    </div>
  );
};

const COUNTRY_PRESETS = [
  { name: 'Turkey',         flag: '🇹🇷', cities: ['Istanbul', 'Ankara', 'Izmir'] },
  { name: 'Germany',        flag: '🇩🇪', cities: ['Berlin', 'Düsseldorf', 'Munich', 'Hamburg', 'Frankfurt'] },
  { name: 'France',         flag: '🇫🇷', cities: ['Paris', 'Lyon', 'Cannes', 'Nice'] },
  { name: 'Italy',          flag: '🇮🇹', cities: ['Milan', 'Bologna', 'Rome', 'Verona'] },
  { name: 'Spain',          flag: '🇪🇸', cities: ['Barcelona', 'Madrid', 'Valencia'] },
  { name: 'UAE',            flag: '🇦🇪', cities: ['Dubai', 'Abu Dhabi'] },
  { name: 'Netherlands',    flag: '🇳🇱', cities: ['Amsterdam', 'Utrecht'] },
  { name: 'United Kingdom', flag: '🇬🇧', cities: ['London', 'Birmingham', 'Manchester'] },
  { name: 'Saudi Arabia',   flag: '🇸🇦', cities: ['Riyadh', 'Jeddah'] },
  { name: 'Switzerland',    flag: '🇨🇭', cities: ['Basel', 'Geneva', 'Zurich'] },
  { name: 'Poland',         flag: '🇵🇱', cities: ['Poznań', 'Warsaw', 'Krakow'] },
  { name: 'Belgium',        flag: '🇧🇪', cities: ['Brussels', 'Ghent'] },
];
const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white';
const labelCls = 'block text-xs font-semibold text-gray-600 mb-1';

export default function ContractorProfile() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => location.state?.tab || 'overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const prevScoreRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');

  const scheduleAutoSave = React.useCallback((patch) => {
    setAutoSaveStatus('saving');
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (!token || !patch || !Object.keys(patch).length) return;
      try {
        await axios.patch(`${API}/api/contractor/profile`, patch, { headers: { Authorization: `Bearer ${token}` } });
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(''), 2500);
      } catch { setAutoSaveStatus(''); }
    }, 1500);
  }, []);

  const [orgSlug, setOrgSlug] = useState('');

  // ── Profile State ──────────────────────────────────────────────────────────
  const [overview, setOverview] = useState({
    about: '', tagline: '', cover_image_url: '', cover_color: '#0B1E4D', logo_url: '',
    stats: { projects_completed: 0, countries_served: 0, happy_clients: 0, years_in_business: 0 }
  });
  const [portfolio, setPortfolio]     = useState([]);
  const [servicesBasic, setServicesBasic]   = useState([]);
  const [servicesDetail, setServicesDetail] = useState([]);
  const [team, setTeam]               = useState([]);
  const [coverage, setCoverage]       = useState({ countries: [], active_shows: [], volume_pricing_shows: [] });
  const [company, setCompany]         = useState({
    capabilities: { max_booth_sqm: 50, avg_budget_min: 10000, avg_budget_max: 50000, currency: 'USD', lead_time_weeks: 4 },
    territories: [],
    response_time: '< 24 hours',
    member_since: '2020',
  });
  const [social, setSocial]           = useState({ linkedin: '', instagram: '', twitter: '', youtube: '', website: '' });
  const [phone, setPhone]             = useState('');
  const [smsPrefs, setSmsPrefs]       = useState({
    new_brief: true,
    proposal_accepted: true,
    proposal_declined: false,
  });
  const [paymentInfo, setPaymentInfo] = useState({
    bank_name: '', account_holder: '', iban: '', swift_bic: '', payment_notes: '',
  });
  const [badges, setBadges]           = useState([]);
  const [stats, setStats]             = useState({ total_sent: 0, total_won: 0, win_rate: 0 });
  const [creditBalance, setCreditBalance] = useState(0);
  const [myShowcase, setMyShowcase]   = useState([]);
  const [featuringId, setFeaturingId] = useState(null); // project being featured
  const [vitrinPickerProjectId, setVitrinPickerProjectId] = useState(null); // which project's image picker is open

  // Fixed toast helper
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Load profile ──────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const { data } = await axios.get(`${API}/api/contractor/profile`, { headers: { Authorization: `Bearer ${token}` } });
        const p = data.profile || {};
        const o = data.org || {};
        setOrgSlug(o.display_name ? o.display_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : '');
        setOverview({
          about: p.about || '',
          tagline: p.tagline || '',
          cover_image_url: p.cover_image_url || '',
          cover_color: p.cover_color || '#0B1E4D',
          logo_url: p.logo_url || '',
          stats: p.stats || { projects_completed: 0, countries_served: 0, happy_clients: 0, years_in_business: 0 }
        });
        setPortfolio((p.portfolio || []).map(item => ({
          ...item,
          // Normalize: if no images array, create from image_url
          images: item.images && item.images.length > 0
            ? item.images
            : item.image_url
              ? [{ id: `img_${item.id}`, url: item.image_url, caption: '' }]
              : [],
        })));
        setServicesBasic(p.services || []);
        setServicesDetail(p.services_detail || []);
        setTeam(p.team || []);
        setCoverage(p.coverage || { countries: [], active_shows: [], volume_pricing_shows: [] });
        setCompany({
          capabilities: p.capabilities || { max_booth_sqm: 50, avg_budget_min: 10000, avg_budget_max: 50000, currency: 'USD', lead_time_weeks: 4 },
          territories: p.territories || [],
          response_time: p.response_time || '< 24 hours',
          member_since: p.member_since || '2020',
        });
        setSocial(p.social || { linkedin: '', instagram: '', twitter: '', youtube: '', website: '' });
        setPhone(p.phone || data.user_phone || '');
        setSmsPrefs(p.sms_notifications || { new_brief: true, proposal_accepted: true, proposal_declined: false });
        setPaymentInfo(p.payment_info || { bank_name: '', account_holder: '', iban: '', swift_bic: '', payment_notes: '' });
        setBadges(data.badges || []);
        setStats(data.stats || { total_sent: 0, total_won: 0, win_rate: 0 });
        // Load credit balance & showcase
        try {
          const [creditRes, showcaseRes] = await Promise.all([
            axios.get(`${API}/api/credits/balance`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${API}/api/contractor/showcase`, { headers: { Authorization: `Bearer ${token}` } }),
          ]);
          setCreditBalance(creditRes.data.balance || 0);
          setMyShowcase(showcaseRes.data.items || []);
        } catch { /* credits optional */ }
        // Init prevScoreRef for confetti baseline
        try {
          const compRes = await axios.get(`${API}/api/contractor/profile-completion`, { headers: { Authorization: `Bearer ${token}` } });
          prevScoreRef.current = compRes.data?.score ?? null;
        } catch { prevScoreRef.current = 0; }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────
  // Auto-save overview on change
  useEffect(() => {
    scheduleAutoSave({ tagline: overview.tagline, about: overview.about, logo_url: overview.logo_url, cover_image_url: overview.cover_image_url });
  // eslint-disable-next-line
  }, [overview.tagline, overview.about, overview.logo_url, overview.cover_image_url]);

  const handleSave = async () => {
    // Validate required fields
    if (!overview.tagline?.trim() && !overview.about?.trim()) {
      showToast('error', t('validation.required') + ' — Tagline veya About bölümünü doldurun');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const payload = {
        services: servicesBasic,
        territories: company.territories,
        capabilities: company.capabilities,
      portfolio: portfolio.map(p => ({
            ...p,
            sqm: p.sqm ? parseInt(p.sqm) : null,
            images: (p.images || []).filter(img => img.url && !img._isLocal),
            // Keep image_url as first image for backward compat
            image_url: (p.images || []).find(img => img.url && !img._isLocal)?.url || p.image_url || null,
          })),
        about: overview.about,
        tagline: overview.tagline,
        cover_image_url: null,          // hero artık renk bazlı
        cover_color: overview.cover_color,
        logo_url: overview.logo_url,
        stats: overview.stats,
        team,
        services_detail: servicesDetail,
        coverage,
        response_time: company.response_time,
        member_since: company.member_since,
        social,
        phone: phone.trim() || null,
        sms_notifications: smsPrefs,
        payment_info: (paymentInfo.bank_name || paymentInfo.iban || paymentInfo.account_holder)
          ? { ...paymentInfo }
          : null,
      };
      await axios.patch(`${API}/api/contractor/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      showToast('success', '✓ Profil başarıyla kaydedildi!');
      // Fire and forget — don't block save on completion check
      axios.get(`${API}/api/contractor/profile-completion`, { headers: { Authorization: `Bearer ${token}` } })
        .then(compRes => {
          const newScore = compRes.data?.score;
          if (newScore === 100 && prevScoreRef.current !== null && prevScoreRef.current < 100) {
            setShowConfetti(true);
          }
          prevScoreRef.current = newScore;
        }).catch(() => {});
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Kaydetme başarısız. Lütfen tekrar deneyin.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" /></div>;


  // ── Fill All Tabs with Demo Data ──────────────────────────────────────────
  const handleFillTestData = () => {
    setOverview({
      tagline: 'Award-winning exhibition stand builders since 2009',
      about: 'ExpoBuilders is a leading full-service exhibition stand construction and design company. With over 15 years of experience across 40+ countries, we deliver turnkey solutions for trade shows, exhibitions, and corporate events worldwide. From concept design to on-site installation and dismantling, our dedicated team ensures every stand reflects our clients\' brand identity with precision and impact.',
      cover_image_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2200&auto=format&fit=crop',
      logo_url: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?q=80&w=200&auto=format&fit=crop',
      stats: { projects_completed: 847, countries_served: 42, happy_clients: 310, years_in_business: 15 }
    });
    setPortfolio([
      { id: 'pf_demo_1', title: 'Hannover Messe 2024 — TechVision AG', client: 'TechVision AG', image_url: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?q=80&w=900&auto=format&fit=crop', sqm: 240, year: 2024, tags: ['Design', 'Build', 'Premium', 'Two-story'] },
      { id: 'pf_demo_2', title: 'BAUMA Munich 2024 — HeavyTech GmbH', client: 'HeavyTech GmbH', image_url: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=900&auto=format&fit=crop', sqm: 560, year: 2024, tags: ['Modular', 'Industrial', 'Outdoor'] },
      { id: 'pf_demo_3', title: 'Dubai GITEX 2023 — NovaTech Solutions', client: 'NovaTech Solutions', image_url: 'https://images.unsplash.com/photo-1540575861501-7cf05a4b125a?q=80&w=900&auto=format&fit=crop', sqm: 180, year: 2023, tags: ['Tech', 'LED', 'Interactive'] },
      { id: 'pf_demo_4', title: 'Salone del Mobile 2023 — Casa Italiana', client: 'Casa Italiana SRL', image_url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=900&auto=format&fit=crop', sqm: 120, year: 2023, tags: ['Luxury', 'Design', 'Furniture'] },
    ]);
    setServicesBasic(['Design', 'Build', 'Logistics', 'AV', 'Electrical']);
    setServicesDetail([
      { id: 'sv_demo_1', title: 'Custom Stand Design', desc: 'Full bespoke stand design from concept to 3D visualization. Our designers create unique, brand-aligned experiences that maximize visitor engagement and ROI.', price: 'From €2,500', icon_name: 'Paintbrush' },
      { id: 'sv_demo_2', title: 'Turnkey Build & Installation', desc: 'Complete stand fabrication, logistics, on-site installation and dismantling. Single point of contact for hassle-free execution across all major European venues.', price: 'From €8,000', icon_name: 'Wrench' },
      { id: 'sv_demo_3', title: 'AV & Digital Integration', desc: 'LED walls, interactive touchscreens, product demo stations, and branded digital content. We bring your stand to life with cutting-edge technology.', price: 'From €1,500', icon_name: 'Zap' },
      { id: 'sv_demo_4', title: 'Modular & Rental Systems', desc: 'Cost-effective modular stands for multi-show exhibitors. Reusable, sustainable, and fully customizable graphic panels — ideal for 2–4 shows per year.', price: 'From €3,500', icon_name: 'ArrowUpDown' },
    ]);
    setTeam([
      { id: 'tm_demo_1', name: 'Marcus Weber', role: 'CEO & Co-Founder', bio: 'Over 20 years in the exhibition industry. Marcus leads global strategy and key client relationships, with a passion for innovative stand design.', avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=200&auto=format&fit=crop&face', linkedin: 'https://linkedin.com/in/marcus-weber-expo' },
      { id: 'tm_demo_2', name: 'Sarah Chen', role: 'Head of Design', bio: 'Award-winning exhibition designer with 12 years of experience. Sarah has led design for Fortune 500 brands at Hannover Messe, CES, and Salone del Mobile.', avatar_url: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?q=80&w=200&auto=format&fit=crop', linkedin: 'https://linkedin.com/in/sarah-chen-design' },
      { id: 'tm_demo_3', name: 'James O\'Brien', role: 'Project Director', bio: 'Coordinates on-site teams across Europe and the Middle East. James ensures every project is delivered on time, on budget, and exceeds client expectations.', avatar_url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=200&auto=format&fit=crop', linkedin: 'https://linkedin.com/in/james-obrien-pm' },
      { id: 'tm_demo_4', name: 'Elena Rossi', role: 'Client Relations Manager', bio: 'Fluent in 4 languages, Elena is the primary point of contact for all European and international clients. Focused on seamless communication and satisfaction.', avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=200&auto=format&fit=crop', linkedin: 'https://linkedin.com/in/elena-rossi-expo' },
    ]);
    setCoverage({
      countries: [
        { name: 'Germany', flag: '🇩🇪', cities: ['Frankfurt', 'Hannover', 'Munich', 'Cologne', 'Dusseldorf'], primary: true },
        { name: 'United Arab Emirates', flag: '🇦🇪', cities: ['Dubai', 'Abu Dhabi'], primary: true },
        { name: 'Italy', flag: '🇮🇹', cities: ['Milan', 'Bologna', 'Rome'], primary: false },
        { name: 'United Kingdom', flag: '🇬🇧', cities: ['London', 'Birmingham', 'Manchester'], primary: false },
        { name: 'France', flag: '🇫🇷', cities: ['Paris', 'Lyon', 'Cannes'], primary: false },
      ],
      active_shows: [
        'Hannover Messe',
        'BAUMA Munich',
        'Ambiente Frankfurt',
        'EuroShop Dusseldorf',
        'GITEX Dubai',
        'Big 5 Dubai',
        'Salone del Mobile Milan',
        'Domotex Hannover',
        'Embedded World Nuremberg',
      ]
    });
    setCompany({
      capabilities: { max_booth_sqm: 800, avg_budget_min: 8000, avg_budget_max: 250000, currency: 'EUR', lead_time_weeks: 6 },
      territories: ['DE', 'AE', 'IT', 'GB', 'FR', 'NL', 'BE', 'CH', 'AT', 'TR'],
      response_time: '< 4 hours',
      member_since: '2009',
    });
    setSocial({
      website: 'https://expobuilders.com',
      linkedin: 'https://linkedin.com/company/expobuilders',
      instagram: 'https://instagram.com/expobuilders',
      twitter: 'https://twitter.com/expobuilders',
      youtube: 'https://youtube.com/@expobuilders',
    });
    showToast('success', 'Tüm sekmeler demo veri ile dolduruldu. Gözden geçirip Kaydet butonuna basın.');
  };

  return (
    <div className="space-y-6 pb-10">
      {showConfetti && <ConfettiBlast onDone={() => setShowConfetti(false)} />}
      {showConfetti && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200 shadow-sm animate-pulse">
          <PartyPopper className="h-6 w-6 text-emerald-600" />
          <div>
            <p className="font-bold text-emerald-800">Profile 100% Complete!</p>
            <p className="text-xs text-emerald-600 mt-0.5">Your profile now has maximum visibility. Exhibitors can find you more easily!</p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your public profile visible to exhibitors</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline" size="sm"
            className="gap-2 border-dashed border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
            onClick={handleFillTestData}
            data-testid="fill-test-data-btn"
          >
            <Wand2 className="h-4 w-4" /> Fill Demo Data
          </Button>
          {orgSlug && (
            <Button variant="outline" size="sm" className="gap-2 text-gray-600" onClick={() => navigate(`/contractor/${orgSlug}`)}>
              <Eye className="h-4 w-4" /> View Public Page
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white gap-2">
            <Save className="h-4 w-4" />{saving ? 'Saving...' : 'Save All Changes'}
          </Button>
          {autoSaveStatus === 'saved' && (
            <span className="text-xs text-green-600 font-medium flex items-center gap-1" data-testid="autosave-indicator">
              <CheckCircle2 className="h-3.5 w-3.5" /> Otomatik kaydedildi
            </span>
          )}
          {autoSaveStatus === 'saving' && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor...
            </span>
          )}
        </div>
      </div>

      {/* ── Fixed Toast Notification ─────────────────────────────────────── */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-3 px-5 py-4 rounded-2xl shadow-2xl text-sm font-medium border transition-all animate-in slide-in-from-bottom-4 ${
          toast.type === 'success'
            ? 'bg-white text-green-800 border-green-200 shadow-green-100'
            : 'bg-white text-red-800 border-red-200 shadow-red-100'
        }`}>
          {toast.type === 'success'
            ? <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            : <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          }
          <span>{toast.msg}</span>
          <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-gray-600">
            <svg className="w-3.5 h-3.5" viewBox="0 0 12 12" fill="currentColor"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeTab === t.id ? 'bg-white text-green-700 shadow-sm border border-green-100' : 'text-gray-500 hover:text-gray-700'}`}>
              <Icon className="h-4 w-4" />{t.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab: Overview ─────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Performance Badges */}
          {badges.length > 0 && (
            <Card className="border-0 shadow-sm bg-gradient-to-r from-emerald-50 to-green-50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-4 h-4 text-emerald-600" />
                  <h3 className="text-sm font-bold text-gray-800">Your Earned Badges</h3>
                  <span className="ml-auto text-xs text-gray-500">{stats.total_sent} proposals · {stats.win_rate}% win rate</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {badges.map(b => {
                    const Icon = b.icon === 'trophy' ? Trophy : b.icon === 'zap' ? Zap : b.icon === 'star' ? Star : Diamond;
                    const color = b.type === 'top_performer' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                                  b.type === 'fast_responder' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                  b.type === 'experienced' ? 'bg-violet-100 text-violet-700 border-violet-200' :
                                  'bg-emerald-100 text-emerald-700 border-emerald-200';
                    return (
                      <div key={b.type} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold ${color}`}
                        data-testid={`badge-${b.type}`}>
                        <Icon className="w-3.5 h-3.5" />{b.label}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4"><CardTitle className="text-sm">About Your Company</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className={labelCls}>Tagline <span className="text-gray-400 font-normal">(short sentence)</span></label>
                <input className={inputCls} placeholder="e.g. Premium exhibition stand builders since 2010" value={overview.tagline}
                  onChange={e => setOverview(p => ({ ...p, tagline: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>About Us <span className="text-gray-400 font-normal">(shown on Overview tab)</span></label>
                <textarea className={`${inputCls} h-36 resize-none`} placeholder="Describe your company, experience, and key strengths..."
                  value={overview.about} onChange={e => setOverview(p => ({ ...p, about: e.target.value }))} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4"><CardTitle className="text-sm">Images</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {/* Cover / Hero Color */}
              <div>
                <label className={labelCls}>Hero Arka Plan Rengi</label>
                <p className="text-xs text-gray-400 mb-3">Profil sayfanızın üst banner rengi. Renk seçin:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    '#0B1E4D', '#1a1a2e', '#1e3a5f', '#0f2027',
                    '#2d1b69', '#4a0e0e', '#1b4332', '#1a1a1a',
                    '#2c3e50', '#b45309', '#1e40af', '#047857',
                    '#7c2d12', '#4c1d95', '#374151', '#be185d',
                  ].map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setOverview(p => ({ ...p, cover_color: color }))}
                      className={`w-9 h-9 rounded-lg border-2 transition-all ${overview.cover_color === color ? 'border-green-500 scale-110 shadow-lg' : 'border-transparent hover:border-gray-300'}`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
                {/* Preview */}
                <div
                  className="mt-3 rounded-xl h-20 flex items-end p-3 transition-colors"
                  style={{ backgroundColor: overview.cover_color || '#0B1E4D' }}
                >
                  <span className="text-white/70 text-xs">Önizleme — banner bu renkte görünecek</span>
                </div>
              </div>
              {/* Company Logo */}
              <div>
                <label className={labelCls}>Company Logo</label>
                <div className="flex items-center gap-4">
                  <ProfileImageUpload
                    value={overview.logo_url}
                    onChange={(path) => setOverview(p => ({ ...p, logo_url: path }))}
                    onDelete={() => setOverview(p => ({ ...p, logo_url: null }))}
                    assetType="logo"
                    entityId="logo"
                    shape="circle"
                    label="Upload Logo"
                  />
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Square PNG or WEBP recommended.<br />Max 300×300 px after processing.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardHeader className="pb-4"><CardTitle className="text-sm">Key Stats</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'projects_completed', label: 'Projects Completed' },
                { key: 'countries_served',   label: 'Countries Served' },
                { key: 'happy_clients',      label: 'Happy Clients' },
                { key: 'years_in_business',  label: 'Years in Business' },
              ].map(s => (
                <div key={s.key}>
                  <label className={labelCls}>{s.label}</label>
                  <input type="number" min="0" className={inputCls} value={overview.stats[s.key]}
                    onChange={e => setOverview(p => ({ ...p, stats: { ...p.stats, [s.key]: parseInt(e.target.value) || 0 } }))} />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
          </div>
      )}

      {/* ── Tab: Portfolio ────────────────────────────────────────────────── */}
      {activeTab === 'portfolio' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                <Coins className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold text-amber-700">{creditBalance} kredi</span>
              </div>
              <a href="/app/contractor/credits" className="text-xs text-amber-600 hover:underline flex items-center gap-1">
                <Sparkles className="w-3 h-3" />Kredi satın al
              </a>
            </div>
            <Button onClick={() => setPortfolio(p => [...p, emptyPortfolioItem()])} className="bg-green-600 hover:bg-green-700 text-white gap-2">
              <Plus className="h-4 w-4" />Add Project
            </Button>
          </div>
          {portfolio.length === 0 && (
            <div className="text-center py-16 text-gray-400 border-2 border-dashed rounded-xl">
              <GalleryHorizontal className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="font-medium">No portfolio items yet</p>
              <p className="text-sm">Click "Add Project" to showcase your work</p>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {portfolio.map((item, i) => {
              const now = new Date().toISOString();
              const showcaseEntry = myShowcase.find(s => s.project_id === item.id && s.status === 'active' && s.end_date > now);
              const daysLeft = showcaseEntry ? Math.max(0, Math.round((new Date(showcaseEntry.end_date) - new Date()) / (1000*60*60*24))) : 0;

              const handleFeature = async (selectedImageUrl) => {
                if (!item.id || featuringId) return;
                if (creditBalance < 100) {
                  alert('Yetersiz kredi. Vitrine eklemek için 100 kredi gereklidir. Kredi satın almak için Kredi & Vitrin sayfasına gidin.');
                  return;
                }
                setFeaturingId(item.id);
                const authToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
                try {
                  const res = await axios.post(`${API}/api/contractor/showcase/feature`,
                    { project_id: item.id, selected_image: selectedImageUrl || null },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                  );
                  showToast('success', res.data.message);
                  setCreditBalance(res.data.remaining_balance);
                  setMyShowcase(prev => [...prev, { project_id: item.id, status: 'active', end_date: res.data.end_date, id: res.data.item_id }]);
                } catch (e) {
                  showToast('error', e.response?.data?.detail || 'Vitrine eklenemedi');
                } finally {
                  setFeaturingId(null);
                }
              };

              // Vitrin image picker: if multiple images, show picker modal
              const showVitrinPicker = vitrinPickerProjectId === item.id;
              const validImages = (item.images || []).filter(img => img.url && !img._isLocal);
              const handleVitrinClick = () => {
                if (validImages.length > 1) setVitrinPickerProjectId(item.id);
                else handleFeature(validImages[0]?.url || null);
              };

              return (
              <Card key={item.id} className="border-0 shadow-sm">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm">Proje {i + 1}</CardTitle>
                  <div className="flex items-center gap-1.5">
                    {showcaseEntry ? (
                      <span className="flex items-center gap-1 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                        <Sparkles className="w-3 h-3" />{daysLeft} gün vitrinde
                      </span>
                    ) : item.id && item.id.startsWith('pf_') === false ? (
                      <button
                        onClick={handleVitrinClick}
                        disabled={!!featuringId}
                        title="100 kredi karşılığında 30 gün ana sayfada öne çıkar"
                        className="flex items-center gap-1 text-xs bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 rounded-full font-medium transition-colors disabled:opacity-50"
                      >
                        <Coins className="w-3 h-3" />Vitrine Ekle
                      </button>
                    ) : null}
                    <button onClick={() => setPortfolio(p => p.filter((_, idx) => idx !== i))} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Proje Başlığı *</label>
                      <input className={inputCls} placeholder="Hannover Messe 2024" value={item.title}
                        onChange={e => setPortfolio(p => p.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>Müşteri Adı</label>
                      <input className={inputCls} placeholder="Acme Corp" value={item.client || ''}
                        onChange={e => setPortfolio(p => p.map((x, idx) => idx === i ? { ...x, client: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>Stand Alanı (m²)</label>
                      <input type="number" className={inputCls} placeholder="120" value={item.sqm || ''}
                        onChange={e => setPortfolio(p => p.map((x, idx) => idx === i ? { ...x, sqm: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>Yıl</label>
                      <input type="number" className={inputCls} placeholder="2024" value={item.year || ''}
                        onChange={e => setPortfolio(p => p.map((x, idx) => idx === i ? { ...x, year: parseInt(e.target.value) || null } : x))} />
                    </div>
                  </div>

                  {/* Multi-image gallery */}
                  <PortfolioGallery
                    images={item.images || []}
                    entityId={item.id}
                    onChange={(updater) => setPortfolio(p => p.map((x, idx) => idx === i ? {
                      ...x,
                      images: typeof updater === 'function' ? updater(x.images || []) : updater
                    } : x))}
                  />

                  <div>
                    <label className={labelCls}>Etiketler <span className="text-gray-400 font-normal">(virgülle ayır)</span></label>
                    <input className={inputCls} placeholder="Design, Build, Premium" value={(item.tags || []).join(', ')}
                      onChange={e => setPortfolio(p => p.map((x, idx) => idx === i ? { ...x, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) } : x))} />
                  </div>
                  <div>
                    <label className={labelCls}>Açıklama</label>
                    <textarea rows={2} className={`${inputCls} resize-none`} placeholder="Proje hakkında kısa açıklama..." value={item.description || ''}
                      onChange={e => setPortfolio(p => p.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} />
                  </div>

                  {/* Vitrin image picker modal */}
                  {showVitrinPicker && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setVitrinPickerProjectId(null)}>
                      <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
                        <p className="font-semibold text-gray-900 mb-3">Vitrine hangi resim çıksın?</p>
                        <div className="grid grid-cols-3 gap-2 mb-4">
                          {validImages.map((img, vi) => {
                            const src = resolveGalleryUrlHelper(img.url);
                            return (
                              <button key={img.id} onClick={() => { setVitrinPickerProjectId(null); handleFeature(img.url); }}
                                className="relative aspect-[4/3] rounded-xl overflow-hidden border-2 border-gray-100 hover:border-amber-400 transition-all">
                                <img src={src} alt="" className="w-full h-full object-cover" />
                                {vi === 0 && <span className="absolute top-1 left-1 bg-amber-400 text-white text-[9px] font-bold px-1 py-0.5 rounded-full">Kapak</span>}
                              </button>
                            );
                          })}
                        </div>
                        <button onClick={() => setVitrinPickerProjectId(null)} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700">İptal</button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Tab: Services ─────────────────────────────────────────────────── */}
      {activeTab === 'services' && (
        <div className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-sm">Service Categories</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {SERVICE_OPTIONS.map(s => (
                  <label key={s} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${servicesBasic.includes(s) ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input type="checkbox" className="hidden" checked={servicesBasic.includes(s)}
                      onChange={e => setServicesBasic(prev => e.target.checked ? [...prev, s] : prev.filter(x => x !== s))} />
                    <span className="text-xs font-medium">{s}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-sm">Detailed Service Cards <span className="text-gray-400 font-normal">(shown in Services tab)</span></h3>
              <Button onClick={() => setServicesDetail(p => [...p, emptyServiceDetail()])} className="bg-green-600 hover:bg-green-700 text-white gap-2 h-8 text-xs px-3">
                <Plus className="h-3.5 w-3.5" />Add Service
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {servicesDetail.map((svc, i) => (
                <Card key={svc.id} className="border-0 shadow-sm">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-xs text-gray-500">Service Card {i + 1}</CardTitle>
                    <button onClick={() => setServicesDetail(p => p.filter((_, idx) => idx !== i))} className="p-1 rounded hover:bg-red-50 text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Title</label>
                        <input className={inputCls} placeholder="Stand Design" value={svc.title}
                          onChange={e => setServicesDetail(p => p.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} />
                      </div>
                      <div>
                        <label className={labelCls}>Price / From</label>
                        <input className={inputCls} placeholder="From $2,500" value={svc.price || ''}
                          onChange={e => setServicesDetail(p => p.map((x, idx) => idx === i ? { ...x, price: e.target.value } : x))} />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Description</label>
                      <textarea className={`${inputCls} h-16 resize-none`} placeholder="Brief description of this service..."
                        value={svc.desc || ''} onChange={e => setServicesDetail(p => p.map((x, idx) => idx === i ? { ...x, desc: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>Icon</label>
                      <select className={inputCls} value={svc.icon_name || 'Wrench'}
                        onChange={e => setServicesDetail(p => p.map((x, idx) => idx === i ? { ...x, icon_name: e.target.value } : x))}>
                        {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Team ─────────────────────────────────────────────────────── */}
      {activeTab === 'team' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setTeam(p => [...p, emptyTeamMember()])} className="bg-green-600 hover:bg-green-700 text-white gap-2">
              <Plus className="h-4 w-4" />Add Team Member
            </Button>
          </div>
          {team.length === 0 && (
            <div className="text-center py-16 text-gray-400 border-2 border-dashed rounded-xl">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="font-medium">No team members yet</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {team.map((member, i) => (
              <Card key={member.id} className="border-0 shadow-sm">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-3">
                    {(member.avatar_url ? resolveAssetUrl(member.avatar_url) : null)
                      ? <img src={resolveAssetUrl(member.avatar_url)} alt="" className="w-10 h-10 rounded-full object-cover border" onError={e => e.target.style.display='none'} />
                      : <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-sm">{member.name?.[0] || '?'}</div>
                    }
                    <span className="text-sm font-semibold text-gray-700">{member.name || `Member ${i + 1}`}</span>
                  </div>
                  <button onClick={() => setTeam(p => p.filter((_, idx) => idx !== i))} className="p-1 rounded hover:bg-red-50 text-red-400"><Trash2 className="h-4 w-4" /></button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Avatar upload */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <ProfileImageUpload
                        value={member.avatar_url}
                        onChange={(path) => setTeam(p => p.map((x, idx) => idx === i ? { ...x, avatar_url: path } : x))}
                        onDelete={() => setTeam(p => p.map((x, idx) => idx === i ? { ...x, avatar_url: null } : x))}
                        assetType="avatar"
                        entityId={member.id}
                        shape="circle"
                        label="Upload Photo"
                      />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Full Name *</label>
                          <input className={inputCls} placeholder="John Smith" value={member.name}
                            onChange={e => setTeam(p => p.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} />
                        </div>
                        <div>
                          <label className={labelCls}>Role / Title *</label>
                          <input className={inputCls} placeholder="Lead Designer" value={member.role}
                            onChange={e => setTeam(p => p.map((x, idx) => idx === i ? { ...x, role: e.target.value } : x))} />
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>LinkedIn URL</label>
                        <input className={inputCls} placeholder="https://linkedin.com/in/..." value={member.linkedin || ''}
                          onChange={e => setTeam(p => p.map((x, idx) => idx === i ? { ...x, linkedin: e.target.value } : x))} />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Bio</label>
                    <textarea className={`${inputCls} h-16 resize-none`} placeholder="Short bio..." value={member.bio || ''}
                      onChange={e => setTeam(p => p.map((x, idx) => idx === i ? { ...x, bio: e.target.value } : x))} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab: Coverage ─────────────────────────────────────────────────── */}
      {activeTab === 'coverage' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Countries */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 text-sm">Coverage Countries</h3>
              <Button onClick={() => setCoverage(p => ({ ...p, countries: [...p.countries, emptyCoverageCountry()] }))}
                className="bg-green-600 hover:bg-green-700 text-white gap-2 h-8 text-xs px-3">
                <Plus className="h-3.5 w-3.5" />Add Country
              </Button>
            </div>

            {/* Quick-add presets */}
            <div className="bg-green-50 rounded-xl p-3 border border-green-100">
              <p className="text-[11px] font-semibold text-green-700 mb-2">Quick Add — Popular Expo Countries</p>
              <div className="flex flex-wrap gap-1.5">
                {COUNTRY_PRESETS.map(preset => {
                  const alreadyAdded = coverage.countries.some(c => c.name === preset.name);
                  return (
                    <button key={preset.name} disabled={alreadyAdded}
                      onClick={() => !alreadyAdded && setCoverage(p => ({ ...p, countries: [...p.countries, { name: preset.name, flag: preset.flag, cities: preset.cities, primary: false }] }))}
                      className={`text-xs px-2 py-1 rounded-full border transition-all ${alreadyAdded ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-200 hover:border-green-400 hover:text-green-700 cursor-pointer'}`}>
                      {preset.flag} {preset.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {coverage.countries.length === 0 && (
              <div className="text-center py-10 text-gray-400 border-2 border-dashed rounded-xl">
                <Globe className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No countries added yet — use the quick-add above!</p>
              </div>
            )}
            {coverage.countries.map((c, i) => (
              <Card key={i} className="border-0 shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="grid grid-cols-2 gap-3 flex-1 mr-3">
                      <div>
                        <label className={labelCls}>Country Name</label>
                        <input className={inputCls} placeholder="Germany" value={c.name}
                          onChange={e => setCoverage(p => ({ ...p, countries: p.countries.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x) }))} />
                      </div>
                      <div>
                        <label className={labelCls}>Flag Emoji</label>
                        <input className={inputCls} placeholder="🇩🇪" value={c.flag || ''}
                          onChange={e => setCoverage(p => ({ ...p, countries: p.countries.map((x, idx) => idx === i ? { ...x, flag: e.target.value } : x) }))} />
                      </div>
                    </div>
                    <button onClick={() => setCoverage(p => ({ ...p, countries: p.countries.filter((_, idx) => idx !== i) }))} className="mt-4 p-1 rounded hover:bg-red-50 text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div>
                    <label className={labelCls}>Cities <span className="text-gray-400 font-normal">(comma separated)</span></label>
                    <input className={inputCls} placeholder="Berlin, Hamburg, Munich" value={(c.cities || []).join(', ')}
                      onChange={e => setCoverage(p => ({ ...p, countries: p.countries.map((x, idx) => idx === i ? { ...x, cities: e.target.value.split(',').map(t => t.trim()).filter(Boolean) } : x) }))} />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={c.primary} onChange={e => setCoverage(p => ({ ...p, countries: p.countries.map((x, idx) => idx === i ? { ...x, primary: e.target.checked } : x) }))} />
                    Primary market
                  </label>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Right column: Active Trade Shows + Volume Pricing Shows */}
          <div className="space-y-4">

            {/* Active Trade Shows — Smart Fair Picker */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-800 text-sm">Active Trade Shows</h3>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <FairPicker
                    selected={coverage.active_shows || []}
                    onChange={shows => setCoverage(p => ({ ...p, active_shows: shows }))}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Volume Pricing Shows */}
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
                  Volume Pricing Shows
                </h3>
                <p className="text-xs text-gray-500 mt-1">Shows where you'll already have a team on-site — offer better rates to exhibitors attending the same event.</p>
              </div>
              <Card className="border-0 shadow-sm border-l-4 border-l-orange-400">
                <CardContent className="p-4 space-y-3">
                  <FairPicker
                    selected={coverage.volume_pricing_shows || []}
                    onChange={shows => setCoverage(p => ({ ...p, volume_pricing_shows: shows }))}
                  />
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      )}

      {/* ── Tab: Company ──────────────────────────────────────────────────── */}
      {activeTab === 'company' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4"><CardTitle className="text-sm">Capabilities & Pricing</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Max Stand Size (m²)</label>
                  <input type="number" className={inputCls} value={company.capabilities.max_booth_sqm}
                    onChange={e => setCompany(p => ({ ...p, capabilities: { ...p.capabilities, max_booth_sqm: parseInt(e.target.value) || 50 } }))} />
                </div>
                <div>
                  <label className={labelCls}>Lead Time (weeks)</label>
                  <input type="number" className={inputCls} value={company.capabilities.lead_time_weeks}
                    onChange={e => setCompany(p => ({ ...p, capabilities: { ...p.capabilities, lead_time_weeks: parseInt(e.target.value) || 4 } }))} />
                </div>
                <div>
                  <label className={labelCls}>Budget Min</label>
                  <input type="number" className={inputCls} value={company.capabilities.avg_budget_min}
                    onChange={e => setCompany(p => ({ ...p, capabilities: { ...p.capabilities, avg_budget_min: parseFloat(e.target.value) || 0 } }))} />
                </div>
                <div>
                  <label className={labelCls}>Budget Max</label>
                  <input type="number" className={inputCls} value={company.capabilities.avg_budget_max}
                    onChange={e => setCompany(p => ({ ...p, capabilities: { ...p.capabilities, avg_budget_max: parseFloat(e.target.value) || 0 } }))} />
                </div>
                <div>
                  <label className={labelCls}>Currency</label>
                  <select className={inputCls} value={company.capabilities.currency}
                    onChange={e => setCompany(p => ({ ...p, capabilities: { ...p.capabilities, currency: e.target.value } }))}>
                    {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Member Since (year)</label>
                  <input className={inputCls} placeholder="2018" value={company.member_since || ''}
                    onChange={e => setCompany(p => ({ ...p, member_since: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>Currency</label>
                  <select className={inputCls} value={company.capabilities.currency}
                    onChange={e => setCompany(p => ({ ...p, capabilities: { ...p.capabilities, currency: e.target.value } }))}>
                    {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Member Since (year)</label>
                  <input className={inputCls} placeholder="2018" value={company.member_since || ''}
                    onChange={e => setCompany(p => ({ ...p, member_since: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Response Time</label>
                <input className={inputCls} placeholder="< 24 hours" value={company.response_time || ''}
                  onChange={e => setCompany(p => ({ ...p, response_time: e.target.value }))} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4"><CardTitle className="text-sm">Operating Territories</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-gray-400 mb-3">Enter 2-letter country codes, one per line (e.g. DE, US, TR)</p>
              <textarea className={`${inputCls} h-36 resize-none`} placeholder={"DE\nUS\nAE\nTR\nFR"}
                value={(company.territories || []).join('\n')}
                onChange={e => setCompany(p => ({ ...p, territories: e.target.value.split('\n').map(t => t.trim().toUpperCase()).filter(Boolean) }))} />
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(company.territories || []).map(t => <Badge key={t} className="text-xs bg-green-50 text-green-700 border-green-200">{t}</Badge>)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Tab: Social ───────────────────────────────────────────────────── */}
      {activeTab === 'social' && (
        <Card className="border-0 shadow-sm max-w-2xl">
          <CardHeader className="pb-4"><CardTitle className="text-sm">Social Media & Web Presence</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: 'website',   label: 'Website URL',  placeholder: 'https://yourcompany.com' },
              { key: 'linkedin',  label: 'LinkedIn',     placeholder: 'https://linkedin.com/company/...' },
              { key: 'instagram', label: 'Instagram',    placeholder: 'https://instagram.com/...' },
              { key: 'twitter',   label: 'Twitter / X',  placeholder: 'https://twitter.com/...' },
              { key: 'youtube',   label: 'YouTube',      placeholder: 'https://youtube.com/@...' },
            ].map(field => (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <div className="flex gap-2">
                  <input className={inputCls} placeholder={field.placeholder} value={social[field.key] || ''}
                    onChange={e => setSocial(p => ({ ...p, [field.key]: e.target.value }))} />
                  {social[field.key] && (
                    <a href={social[field.key]} target="_blank" rel="noreferrer" className="p-2 border rounded-lg hover:bg-gray-50 shrink-0">
                      <ExternalLink className="h-4 w-4 text-gray-400" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── Tab: Payment Info ─────────────────────────────────────────────── */}
      {activeTab === 'payment' && (
        <Card className="border-0 shadow-sm max-w-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-green-600" />Fatura Ödeme Bilgileri
            </CardTitle>
            <p className="text-xs text-gray-400 mt-1">Bu bilgiler faturalarınızın PDF çıktısında gösterilir. Müşterileriniz ödeme yaparken bu bilgileri kullanır.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Banka Adı</label>
                <input className={inputCls} placeholder="Ziraat Bankası"
                  value={paymentInfo.bank_name}
                  onChange={e => setPaymentInfo(p => ({ ...p, bank_name: e.target.value }))}
                  data-testid="payment-bank-name" />
              </div>
              <div>
                <label className={labelCls}>Hesap Sahibi</label>
                <input className={inputCls} placeholder="Quattrostand A.Ş."
                  value={paymentInfo.account_holder}
                  onChange={e => setPaymentInfo(p => ({ ...p, account_holder: e.target.value }))}
                  data-testid="payment-account-holder" />
              </div>
            </div>
            <div>
              <label className={labelCls}>IBAN</label>
              <input className={inputCls} placeholder="TR12 0001 0017 4531 0823 1045 39"
                value={paymentInfo.iban}
                onChange={e => setPaymentInfo(p => ({ ...p, iban: e.target.value.toUpperCase() }))}
                data-testid="payment-iban" />
            </div>
            <div>
              <label className={labelCls}>SWIFT / BIC Kodu</label>
              <input className={inputCls} placeholder="TCZBTR2A"
                value={paymentInfo.swift_bic}
                onChange={e => setPaymentInfo(p => ({ ...p, swift_bic: e.target.value.toUpperCase() }))}
                data-testid="payment-swift" />
            </div>
            <div>
              <label className={labelCls}>Ödeme Notu (isteğe bağlı)</label>
              <textarea className={inputCls + " resize-none"} rows={3}
                placeholder="Ödeme açıklamasına fatura numarasını yazınız."
                value={paymentInfo.payment_notes}
                onChange={e => setPaymentInfo(p => ({ ...p, payment_notes: e.target.value }))}
                data-testid="payment-notes" />
            </div>
            {(paymentInfo.bank_name || paymentInfo.iban) && (
              <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">PDF Önizleme</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {paymentInfo.bank_name && <div><span className="text-gray-400">Banka:</span> <span className="font-medium">{paymentInfo.bank_name}</span></div>}
                  {paymentInfo.account_holder && <div><span className="text-gray-400">Hesap:</span> <span className="font-medium">{paymentInfo.account_holder}</span></div>}
                  {paymentInfo.iban && <div className="col-span-2"><span className="text-gray-400">IBAN:</span> <span className="font-mono font-medium">{paymentInfo.iban}</span></div>}
                  {paymentInfo.swift_bic && <div><span className="text-gray-400">SWIFT:</span> <span className="font-medium">{paymentInfo.swift_bic}</span></div>}
                  {paymentInfo.payment_notes && <div className="col-span-2 text-gray-500 italic">{paymentInfo.payment_notes}</div>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}


      {/* ── Tab: Notifications ────────────────────────────────────────────── */}
      {activeTab === 'notifications' && (
        <Card className="border-0 shadow-sm max-w-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Bell className="h-4 w-4 text-green-600" />SMS Notification Preferences
            </CardTitle>
            <p className="text-xs text-gray-400 mt-1">Receive instant SMS alerts to your mobile phone when important events occur.</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Phone number */}
            <div>
              <label className={labelCls}>Mobile Phone Number (E.164 format)</label>
              <input
                className={inputCls}
                placeholder="+905551234567 or +49123456789"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                data-testid="phone-input"
              />
              <p className="text-xs text-gray-400 mt-1">Include country code, e.g. +90 for Turkey, +49 for Germany.</p>
            </div>

            {/* SMS preference toggles */}
            <div>
              <label className={labelCls}>Notification Types</label>
              <div className="space-y-3 mt-2">
                {[
                  { key: 'new_brief',           label: 'New Brief Available',     desc: 'Get notified when a new brief is published in your region', icon: <BriefcaseIcon className="h-4 w-4 text-blue-500" /> },
                  { key: 'proposal_accepted',   label: 'Proposal Accepted',       desc: 'Immediate alert when an exhibitor accepts your quote', icon: <ThumbsUp className="h-4 w-4 text-green-500" /> },
                  { key: 'proposal_declined',   label: 'Proposal Not Selected',   desc: 'Notification when your proposal was not chosen', icon: <ThumbsDown className="h-4 w-4 text-gray-400" /> },
                ].map(pref => (
                  <div key={pref.key}
                    className={`flex items-center justify-between p-4 rounded-xl border transition-colors cursor-pointer
                      ${smsPrefs[pref.key] ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                    onClick={() => setSmsPrefs(p => ({ ...p, [pref.key]: !p[pref.key] }))}
                    data-testid={`sms-pref-${pref.key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{pref.icon}</div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{pref.label}</p>
                        <p className="text-xs text-gray-500">{pref.desc}</p>
                      </div>
                    </div>
                    <div className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${smsPrefs[pref.key] ? 'bg-green-600' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${smsPrefs[pref.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Twilio status badge */}
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <p className="text-xs text-green-700 font-medium">SMS service active (Twilio)</p>
              {!phone && <p className="text-xs text-amber-600 ml-auto">⚠ Add phone number to receive SMS</p>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
